import { removeProjectError } from 'actions/project'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { rolesSelectors } from 'ducks/auth_roles'
import { orgSelectors } from 'ducks/orgs'
import { paymentOptionSelectionSelectors } from 'ducks/paymentOptionSelection'
import { projectMilestonesSelectors, saveProjectMilestones, setProjectMilestonesLoading } from 'ducks/projectMilestones'
import { PaymentMethodType } from 'myenergy/dialogs/checkout/cashFlow/types'
import { useSortedProjectErrors } from 'projectSections/errors/useSortedProjectErrors'
import { addProjectErrorToReduxStoreDirect } from 'projectSections/hooks/useAddProjectErrors'
import { useEffectiveCashflowContext } from 'projectSections/hooks/useEffectiveCashflowContext'
import { useExpectedMilestonesForSoldProject } from 'projectSections/hooks/useExpectedMilestonesForSoldProject'
import { fetchProjectInvoice } from 'projectSections/sections/manage/cashFlowTransactions/invoices/utils'
import { useErrorHandling, useNotify, useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, useFormState } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'
import { PaymentRequestType } from 'types/cashFlow'
import { InvoicePaymentRequestType } from 'types/cashFlow/invoices'
import { StudioSystemType } from 'types/global'
import { ExpectedMilestonePaymentType, PaymentOptionDataType } from 'types/paymentOptions'
import { useGetIsCashFlowVisible } from 'util/cashFlow'
import { useIsProjectUsingCashFlow } from '../hooks'
import { useIsCalculating } from '../projectProgress/cashFlow/utils'
import { PAYABLE_REQUEST_STATUS_VALUES } from './constants'

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const restClientInstance = restClient(window.API_ROOT + '/api')

/**
 * Fetches all payment requests for a project and saves the resuts in redux to avoid duplicate requests
 * @param  {number} projectId The id of the project
 * @param {number | undefined} orgIdFromPaymentOption The id of the org whose payment options are being used which with Teams may not be the logged in org
 */
export const useGetAllPaymentRequestsForProject = (projectId: number, orgIdFromPaymentOption: number | undefined) => {
  const [sentPaymentRequests, setSentPaymentRequests] = useState<PaymentRequestType[]>([])

  const loggedInOrgId = useSelector(orgSelectors.getOrg)?.id
  const milestoneRefreshTrigger = useSelector(projectMilestonesSelectors.getRefreshMilestonesTrigger)
  const cashFlowIsVisible = useGetIsCashFlowVisible()

  const dispatch = useDispatch()
  const notify = useNotify()
  const translate = useTranslate()

  const fetchSentRequests = useCallback(() => {
    // don't fetch if we don't have a project id or this org doesn't have access to CashFlow
    if (projectId && cashFlowIsVisible) {
      dispatch(setProjectMilestonesLoading())
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgIdFromPaymentOption || loggedInOrgId}/payments/?project_id=${projectId}`,
      })
        .then((res) => {
          setSentPaymentRequests(res.data.results)
          dispatch(saveProjectMilestones(res.data.results, projectId))
        })
        .catch((err) => {
          notify(translate('We were unable to retrieve the list of invoices for this project.'), 'warning')
          dispatch(saveProjectMilestones([], projectId))
        })
    }
  }, [projectId, cashFlowIsVisible])

  useEffect(() => {
    fetchSentRequests()
  }, [projectId, milestoneRefreshTrigger, cashFlowIsVisible])

  return sentPaymentRequests
}

// Minimal shape this hook actually reads off `system` at runtime: just the system uuid
// and `userData.id` (used when matching sent payment requests to a system). Accept this
// instead of the full StudioSystemType so callers can pass either the in-editor system
// or the proposalData system shape (SystemDataType) without unsafe casts.
export interface PaymentRequestsSystem {
  uuid: string
  userData?: { id?: number }
}

const milestoneToUnsentPaymentRequest = (
  milestone: ExpectedMilestonePaymentType,
  projectId: number,
  paymentOption: PaymentOptionDataType,
  system: PaymentRequestsSystem
): PaymentRequestType => ({
  archived: milestone.archived ?? false,
  amount_requested: milestone.payment_amount,
  amount_paid: 0,
  payment_method: null,
  currency: milestone.currency,
  date_completed: milestone.date_completed,
  date_refunded: null,
  date_chargeback_completed: null,
  date_requested: milestone.date_requested,
  date_due: milestone.date_due,
  date_paid_out: null,
  id: null,
  payment_number: milestone.payment_number,
  org: paymentOption.org_id!,
  payment_collection_url: null,
  payment_milestone_configuration: milestone.payment_milestone_configuration_id,
  payment_option: paymentOption.id,
  project: projectId,
  psp_integration: 'bluesnap',
  status: 'unsent',
  system: system.userData?.id ?? 0,
  is_standalone:
    milestone.payment_milestone_configuration_id === null &&
    (milestone.override_uuid === null || !milestone.override_uuid),
  title: milestone.title,
  payment_milestone_override_uuid: milestone.override_uuid,
  percentage: milestone.percentage,
  paying_party_type: milestone.paying_party_type,
  paying_party_name: milestone.paying_party_name,
  is_deposit:
    milestone.is_deposit ||
    Boolean(milestone.payment_number === 1 && milestone.override_uuid && !milestone.payment_milestone_configuration_id),
})

const TERMINAL_DEPOSIT_STATUSES = ['cancelled', 'refunded', 'chargeback complete', 'paid in full', 'completed']

/** Scope a payment request to the card's payment option (and system when known). */
export const paymentRequestMatchesOption = (
  req: PaymentRequestType,
  paymentOptionId: number | undefined,
  systemUserDataId: number | undefined
): boolean => {
  if (paymentOptionId != null && req.payment_option != null && req.payment_option !== paymentOptionId) {
    return false
  }
  if (systemUserDataId != null && req.system != null && req.system !== 0 && req.system !== systemUserDataId) {
    return false
  }
  return true
}

const isSentPaymentRequest = (req: PaymentRequestType) => req.id != null && req.status !== 'unsent'

/** First deposit / payment #1 request for this payment option (excludes cancelled/reversed). */
export const findFirstDepositPaymentRequestForOption = (
  requests: PaymentRequestType[] | undefined,
  paymentOptionId: number,
  systemUserDataId: number | undefined,
  options?: { excludeStandalone?: boolean; sentOnly?: boolean; pendingOnly?: boolean }
): PaymentRequestType | undefined => {
  if (!requests?.length) return undefined
  return requests.find((req) => {
    if (!paymentRequestMatchesOption(req, paymentOptionId, systemUserDataId)) return false
    if (options?.excludeStandalone && req.is_standalone) return false
    if (options?.sentOnly && !isSentPaymentRequest(req)) return false
    if (options?.pendingOnly && isSentPaymentRequest(req)) return false
    if (req.payment_number !== 1 && !req.is_deposit) return false
    return !TERMINAL_DEPOSIT_STATUSES.includes(req.status)
  })
}

/**
 * Return shape of {@link useGetPaymentRequests}.
 *
 * Note that ``paymentRequests`` is the *flat* array of payment requests including
 * synthesised expected milestones — it is NOT a wrapper object. Callers that
 * destructure ``paymentRequests`` (often renaming it to
 * ``paymentRequestsIncludingUnsent``) must treat it as an array directly; writing
 * ``paymentRequestsIncludingUnsent.paymentRequests`` is a regression that previously
 * hid the Cashflow "Request Payment" CTA for cash options.
 */
export type UseGetPaymentRequestsResult = {
  paymentRequests: PaymentRequestType[]
  isLoading: boolean
  refresh: () => Promise<void> | void
  sentPaymentRequests: PaymentRequestType[]
}

/**
 * Fetches all payment requests and expected milestone payments for a particular system and payment option
 * @param  {PaymentRequestsSystem | undefined} system The system you want milestones for, this is generally the sold system
 * @param {PaymentOptionDataType | undefined} paymentOption The payment option you want milestones for, this is generally the sold payment option
 * @param {boolean} isSold Whether the project is sold. Sold projects will attribute collected payments from other systems/payment options to the provided system/payment option
 */
export const useGetPaymentRequests = (
  projectId: number,
  system: PaymentRequestsSystem | undefined,
  paymentOption: PaymentOptionDataType | undefined,
  isSold: boolean = false,
  isCheckout: boolean = false
): UseGetPaymentRequestsResult => {
  const [sentPaymentRequests, setSentPaymentRequests] = useState<PaymentRequestType[]>([])
  const [paymentRequestIncludingUnsent, setPaymentRequestsIncludingUnsent] = useState<PaymentRequestType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [requestsFetched, setRequestsFetched] = useState<boolean>(false)

  const isCalculating = useIsCalculating(system?.uuid)
  const milestoneRefreshTrigger = useSelector(projectMilestonesSelectors.getRefreshMilestonesTrigger)
  const notify = useNotify()
  const dispatch = useDispatch()
  const translate = useTranslate()
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)
  const savedMilestones = useSelector(projectMilestonesSelectors.getProjectMiletones)
  const savedMilestonesProjectId = useSelector(projectMilestonesSelectors.getProjectMilestonesProjectId)
  const milestonesAreLoading = useSelector(projectMilestonesSelectors.getMilestonesAreLoading)
  const cashFlowIsVisible = useGetIsCashFlowVisible()
  const isCustomer = !useSelector(rolesSelectors.getCurrentRoleId)

  const {
    milestones: soldMilestones,
    adHocPaymentRequests,
    isLoading: soldMilestonesLoading,
  } = useExpectedMilestonesForSoldProject(projectId, system, paymentOption, isSold)

  // all customers are allowed to query the payment requests associated with their project
  // and split doesn't work with customers so we can't rely on a feature flag for them
  const allowedToQueryPayments = isCustomer || cashFlowIsVisible

  const fetchSentRequests = useCallback(() => {
    if (projectId && system?.uuid && paymentOption?.id && !milestonesAreLoading && allowedToQueryPayments) {
      if (savedMilestones && savedMilestonesProjectId === projectId) {
        setSentPaymentRequests(savedMilestones)
        setRequestsFetched(true)
        return
      }
      setIsLoading(true)
      dispatch(setProjectMilestonesLoading())
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${paymentOption.org_id}/payments/?project_id=${projectId}`,
      })
        .then((res) => {
          setSentPaymentRequests(res.data.results)
          setRequestsFetched(true)
          dispatch(saveProjectMilestones(res.data.results, projectId))
        })
        .catch((err) => {
          notify(translate('We were unable to retrieve the list of invoices for this project.'), 'warning')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [
    projectId,
    system?.uuid,
    paymentOption?.id,
    savedMilestones,
    savedMilestonesProjectId,
    milestonesAreLoading,
    allowedToQueryPayments,
  ])

  useEffect(() => {
    setSentPaymentRequests([])
    setPaymentRequestsIncludingUnsent([])
    setRequestsFetched(false)
  }, [projectId])

  useEffect(() => {
    fetchSentRequests()
  }, [projectId, system?.uuid, paymentOption?.id, fetchSentRequests])

  useEffect(() => {
    if (milestoneRefreshTrigger || refreshSystemsTrigger) fetchSentRequests()
  }, [milestoneRefreshTrigger, refreshSystemsTrigger, fetchSentRequests])

  const buildSoldPaymentRequestRows = useCallback(() => {
    if (!system || !paymentOption || !requestsFetched) return

    const referencedPrIds = new Set<number>()
    const combined: PaymentRequestType[] = []

    // Prefer milestone API (CO-aware); fall back to design calcs until the API responds.
    const milestoneSchedule =
      soldMilestones.length > 0
        ? soldMilestones
        : soldMilestonesLoading
          ? []
          : (paymentOption.expected_milestone_payments ?? [])

    milestoneSchedule.forEach((milestone) => {
      if (milestone.payment_request_id) {
        const sentPr = sentPaymentRequests?.find((pr) => pr.id === milestone.payment_request_id)
        if (sentPr) {
          referencedPrIds.add(sentPr.id!)
          combined.push({
            ...sentPr,
            amount_requested: milestone.payment_amount,
            percentage: milestone.percentage,
          })
        }
      } else if (paymentOption.org_id) {
        combined.push(milestoneToUnsentPaymentRequest(milestone, projectId, paymentOption, system))
      }
    })

    adHocPaymentRequests?.forEach((pr) => {
      if (pr.id) referencedPrIds.add(pr.id)
      combined.push(pr)
    })

    combined.sort((a, b) => a.payment_number - b.payment_number)
    setPaymentRequestsIncludingUnsent(combined)
  }, [
    soldMilestones,
    soldMilestonesLoading,
    adHocPaymentRequests,
    sentPaymentRequests,
    requestsFetched,
    system,
    paymentOption,
    projectId,
  ])

  // Sold projects: table rows follow the milestone API (same as progress bar)
  useEffect(() => {
    if (isSold) {
      buildSoldPaymentRequestRows()
    }
  }, [isSold, buildSoldPaymentRequestRows, isCalculating])

  // Unsold / checkout: combine sent payment requests with design JSON milestones
  useEffect(() => {
    if (isSold || !requestsFetched || !system || !paymentOption) return

    let unsentPayments = paymentOption?.expected_milestone_payments?.filter((milestone) => {
      const match = sentPaymentRequests?.find((pmtReq) => {
        // ignore any standalone payment requests that have been sent. Those will always be in the sentPaymentRequests list
        if (pmtReq.is_standalone) return false
        // ignore any payment requests that have been sent but cancelled or reversed, we should still
        // have a record of upcoming payment for those
        if (['cancelled', 'refunded', 'chargeback complete'].includes(pmtReq.status)) return false

        //if the project is not yet sold, do not assign incomplete invoices from other systems/payment options
        // to this system and payment option
        if (!isSold && PAYABLE_REQUEST_STATUS_VALUES.includes(pmtReq.status)) {
          if (paymentOption?.id && pmtReq.payment_option !== paymentOption?.id) {
            return false
          }
          if (system?.userData?.id && pmtReq.system !== system?.userData?.id) {
            return false
          }
          // this is mainly to deal with financed deposits that are injected from the payment option and not the milestone
          // in some circumstances the override ids won't match but they're actually the same milestone
          if (
            pmtReq.is_deposit &&
            milestone.is_deposit &&
            pmtReq.payment_milestone_override_uuid &&
            milestone.override_uuid
          ) {
            return true
          }
        }

        if (pmtReq.payment_milestone_configuration) {
          return (
            pmtReq.payment_milestone_configuration === milestone.payment_milestone_configuration_id ||
            (!PAYABLE_REQUEST_STATUS_VALUES.includes(pmtReq.status) &&
              pmtReq.payment_number === milestone.payment_number)
          )
        } else {
          return (
            pmtReq.payment_milestone_override_uuid === milestone.override_uuid ||
            (!PAYABLE_REQUEST_STATUS_VALUES.includes(pmtReq.status) &&
              pmtReq.payment_number === milestone.payment_number)
          )
        }
      })
      return !match
    })
    let combined: PaymentRequestType[] = []
    combined =
      sentPaymentRequests?.filter((req) => {
        if (system?.userData?.id && paymentOption?.id) {
          return req.payment_option === paymentOption?.id && req.system === system?.userData?.id
        } else {
          // system?.userData?.id is null immediately after accepting the proposal
          // if full calcs finish while viewing the proposal. In that scenario we can't use
          // system to check but just checking payment option and deposit suffices
          return req.payment_option === paymentOption?.id && req.is_deposit && isCheckout
        }
      }) ?? []
    unsentPayments?.forEach((milestone) => {
      if (paymentOption.org_id) {
        combined.push(milestoneToUnsentPaymentRequest(milestone, projectId, paymentOption, system))
      }
    })
    combined = combined.sort((a, b) => a.payment_number - b.payment_number)
    setPaymentRequestsIncludingUnsent(combined)
  }, [
    paymentOption?.expected_milestone_payments,
    sentPaymentRequests,
    requestsFetched,
    isCalculating,
    isSold,
    isCheckout,
    system,
    paymentOption,
    projectId,
  ])

  return {
    paymentRequests: paymentRequestIncludingUnsent,
    isLoading: isLoading || (isSold && soldMilestonesLoading),
    refresh: fetchSentRequests,
    sentPaymentRequests,
  }
}

export const useGetSoldRecords = (project): [StudioSystemType | undefined, PaymentOptionDataType | undefined] => {
  const { system, paymentOption } = useEffectiveCashflowContext(project)
  return [system, paymentOption]
}

/**
 * Used in ProjectForm to get cashflow required customer info error message.
 * Once rendered, automatically adds ProjectError that shows error icon and popover in the Project>Payments tab
 */
export const useCashflowCustomerInfoValidations = () => {
  const project = useFormState().values
  const orgHasCashFlow = useSelector(orgSelectors.getCashFlowIsActive)
  const orgId = useSelector(authSelectors.getOrgId)
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)
  const form = useForm()
  const notify = useNotify()
  const dispatch = useDispatch()
  const fetchSystems = () => window.editor?.getSystems()
  const [isLoading, setIsLoading] = useState(true)
  const [systems, setSystems] = useState<StudioSystemType[]>(fetchSystems())
  const [soldSystem, soldPaymentOption] = useGetSoldRecords(project)
  const isProjectUsingCashflow = useIsProjectUsingCashFlow(soldSystem, soldPaymentOption, refreshSystemsTrigger)
  const hasError = useGetCashflowCustomerInfoError()
  const hasCommercialError = useGetCommercialRequirementsInfoError()
  const errorHandling = useErrorHandling()

  const isCommercialReq = useMemo(() => {
    return !project?.is_residential && !Boolean(project?.invoice_details?.recipient_tax_id)
  }, [project?.is_residential, project?.country_iso2, project?.invoice_details?.recipient_tax_id])

  useEffect(() => {
    if (isCommercialReq) {
      logAmplitudeEvent('commercial_invoice_tax_id_error_shown', {
        project_id: project?.id,
        project_country: project?.country_iso2,
      })
    }
  }, [isCommercialReq])

  const isSold = useMemo(
    () => Boolean(project?.system_sold && project?.payment_option_sold),
    [project?.system_sold, project?.payment_option_sold]
  )

  const showAlert = useMemo(
    () => Boolean(orgHasCashFlow && isProjectUsingCashflow && !isLoading && systems.length),
    [orgHasCashFlow, isLoading, systems.length, isProjectUsingCashflow, refreshSystemsTrigger]
  )

  const COMMERCIAL_REQUIREMENTS_ERROR_MSG =
    "Commercial projects require the invoice recipient's tax ID for compliance purposes. To edit invoice details and enable invoices on this project, %please click here.%"

  useEffect(() => {
    if (refreshSystemsTrigger) {
      setSystems([...fetchSystems()])
    }
  }, [refreshSystemsTrigger])

  const errorMessage = useMemo(() => {
    const recipient =
      project?.contacts_data?.find((e) => e.type === 0) || project?.contacts_new?.find((e) => e.type === 0)
    const invoiceDetails = project?.invoice_details // Overwrites project-level customer info (contacts_data) if invoice details are provided

    const firstName = invoiceDetails?.recipient_first_name || recipient?.first_name
    const lastName = invoiceDetails?.recipient_last_name || recipient?.family_name

    const editSuffix = isSold
      ? 'Please enter these values on the Info page or in the invoice details by clicking the edit invoice details button on the invoices section below.'
      : 'Please enter these values on the Info page or in the invoice details on the CashFlow payment card.'

    if (!firstName) {
      return `Projects with a CashFlow payment method must include customer name. ${editSuffix}`
    } else if (!lastName) {
      return `Projects with a CashFlow payment method must include customer last name. ${editSuffix}`
    } else if (project?.country_iso2 === 'US' && !(invoiceDetails?.billing_address_state || project?.state)) {
      return 'CashFlow payment method must include project country and state (for US-based projects). Please enter these values on the Info page.'
    } else if (isCommercialReq) {
      return COMMERCIAL_REQUIREMENTS_ERROR_MSG
    } else {
      return false
    }
  }, [
    isSold,
    project?.invoice_details,
    project?.invoice_details?.recipient_first_name,
    project?.invoice_details?.recipient_last_name,
    project?.contacts_data,
    project?.contacts_new,
    project?.country_iso2,
    project?.invoice_details?.recipient_tax_id,
    isCommercialReq,
  ])

  useEffect(() => {
    if (orgId && project?.id && project?.id !== 'new' && orgHasCashFlow) {
      fetchProjectInvoice(orgId, project?.id)
        .then((data) => {
          form.change('invoice_details', data)
        })
        .catch(errorHandling.data_provider)
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [orgId, project?.id, orgHasCashFlow])

  useEffect(() => {
    if (hasError) {
      dispatch(
        removeProjectError({
          key: 'CASHFLOW_REQUIRED_CUSTOMER_INFO_ERROR',
          source: 'payment_cashflow',
        })
      )
    }

    if (hasCommercialError) {
      dispatch(
        removeProjectError({
          key: 'CASHFLOW_COMMERCIAL_INVOICING_REQUIREMENTS_ERROR',
          source: 'commercial_requirements',
        })
      )
    }

    if (showAlert) {
      if (errorMessage && errorMessage !== COMMERCIAL_REQUIREMENTS_ERROR_MSG) {
        addProjectErrorToReduxStoreDirect({
          messageType: 'text',
          source: 'payment_cashflow',
          key: 'CASHFLOW_REQUIRED_CUSTOMER_INFO_ERROR',
          message: errorMessage,
          severity: 'error',
          category: 'payment_cashflow',
        })
      } else if (isCommercialReq && errorMessage === COMMERCIAL_REQUIREMENTS_ERROR_MSG) {
        addProjectErrorToReduxStoreDirect({
          messageType: 'commercial_requirements',
          source: 'commercial_requirements',
          key: 'CASHFLOW_COMMERCIAL_INVOICING_REQUIREMENTS_ERROR',
          message: COMMERCIAL_REQUIREMENTS_ERROR_MSG,
          severity: 'error',
          category: 'commercial_requirements',
        })
      } else {
        dispatch(
          removeProjectError({
            key: 'CASHFLOW_COMMERCIAL_INVOICING_REQUIREMENTS_ERROR',
            source: 'commercial_requirements',
          })
        )
      }
    }
  }, [showAlert, errorMessage, refreshSystemsTrigger, isCommercialReq, hasError, hasCommercialError])

  return [errorMessage, isLoading]
}

export const useGetCashflowCustomerInfoError = (): string => {
  // Get the project errors from redux with key CASHFLOW_REQUIRED_CUSTOMER_INFO_ERROR
  const errors = useSortedProjectErrors([
    { keys: ['CASHFLOW_REQUIRED_CUSTOMER_INFO_ERROR'], sources: ['payment_cashflow'] },
  ])

  // Bypass types since we know key CASHFLOW_REQUIRED_CUSTOMER_INFO_ERROR will always have string value
  if (errors.length && errors[0]?.message) return String(errors[0].message)
  return ''
}

export const useGetCommercialRequirementsInfoError = (): string => {
  // Get the project errors from redux with key CASHFLOW_COMMERCIAL_INVOICING_REQUIREMENTS_ERROR
  const errors = useSortedProjectErrors([
    { keys: ['CASHFLOW_COMMERCIAL_INVOICING_REQUIREMENTS_ERROR'], sources: ['commercial_requirements'] },
  ])

  if (errors.length && errors[0]?.message) return String(errors[0].message)
  return ''
}

export const useGetProjectHasCashFlow = () => {
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)

  const hasCashFlowOnThisProject = useMemo(() => {
    const systems = window.editor?.getSystems()
    let foundCashFlow = false
    systems?.forEach((sys) => {
      sys.payment_options?.forEach((pmt) => {
        if (pmt?.expected_milestone_payments?.length > 0) foundCashFlow = true
      })
    })
    return foundCashFlow
  }, [refreshSystemsTrigger])

  return hasCashFlowOnThisProject
}

export const updatePaymentRequestSettings = (
  orgId: number,
  paymentRequestId: number,
  accepted_payment_methods: PaymentMethodType[],
  enable_surcharging: boolean
) => {
  return new Promise((resolve, reject) => {
    restClientInstance('CUSTOM_PATCH', 'custom', {
      url: `orgs/${orgId}/payments/${paymentRequestId}/settings/`,
      data: {
        accepted_payment_methods,
        surcharging_enabled: enable_surcharging,
      },
    })
      .then((res) => {
        resolve(true)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

export const useInvoiceLink = (orgId: number | undefined, paymentRequestId: number | undefined) => {
  const [isFetchingInvoiceURL, setIsFetchingInvoiceURL] = useState<boolean>(false)
  const [downloadUrl, setDownloadUrl] = useState<string>()
  const [pollCount, setPollCount] = useState<number>(0)

  const MAX_POLL_COUNT = 10

  const notify = useNotify()

  const resetInvoiceLink = () => {
    setDownloadUrl(undefined)
    setPollCount(0)
    setIsFetchingInvoiceURL(false)
  }

  useEffect(() => {
    const fetchInvoice = async () => {
      // don't make the request until we have adequate data
      if (!orgId || !paymentRequestId) return
      // if we already have the download URL then don't re-request
      if (downloadUrl) return
      // don't spam our servers
      if (pollCount > MAX_POLL_COUNT) return
      await sleep(pollCount * 500)
      setIsFetchingInvoiceURL(true)
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/payments/${paymentRequestId}/download_url/`,
      })
        .then((res) => {
          if (res.data.download_url) {
            if (!res.data.pdf_download_url_status || res.data.pdf_download_url_status === 'active') {
              setDownloadUrl(res.data.download_url)
            } else {
              setPollCount(pollCount + 1)
            }
          }
        })
        .catch((err) => {
          let message = 'Failed to fetch invoice URL'
          if (typeof err === 'string') {
            message = err
          } else if ('message' in err && typeof err.message === 'string') {
            message = err.message
          }
          notify(message, 'warning')
          console.log('err:', err)
          setPollCount(MAX_POLL_COUNT + 1)
        })
        .finally(() => {
          setIsFetchingInvoiceURL(false)
        })
    }
    fetchInvoice()
  }, [orgId, paymentRequestId, pollCount, downloadUrl])

  return { isFetchingInvoiceURL, downloadUrl, resetInvoiceLink }
}

export const useIsPaymentRequestNext = (
  paymentRequest: PaymentRequestType | InvoicePaymentRequestType,
  allPaymentRequests: PaymentRequestType[]
) => {
  const isNext = useMemo(() => {
    const isCancelledOrReversed = ['cancelled', 'refunded', 'chargeback complete'].includes(paymentRequest?.status)

    if (paymentRequest?.id && !isCancelledOrReversed) return false
    // if it's not sent but it's the first payment then it has to be next
    if (paymentRequest.payment_number === 1 && paymentRequest.status === 'unsent') return true

    // otherwise go see if there is a non-cancelled request for any of the previous milestones
    const previousUnsentRequest = allPaymentRequests?.find(
      (prevPaymentRequest) =>
        !prevPaymentRequest.is_standalone &&
        prevPaymentRequest.payment_number < paymentRequest.payment_number &&
        !prevPaymentRequest.id
    )
    // if any previous milestone has not yet been sent then this can't be the next payment so return false
    return previousUnsentRequest ? false : true
  }, [paymentRequest, allPaymentRequests])
  // if it's already sent then just return false

  return isNext
}
